// __DELAY_VIEW__ (2026-10-30) the hub's delay breakdown for this site, with the signed-in person's token (as
// hubDrafts.js / dayGraphService.js):
//   GET  /api/board/delay-breakdown/<load id>  -> {view}: one job's delay by type (Load Details -> Delay tab)
//   POST /api/board/delay-badges {ids}         -> {badges: {load id: billable minutes}} (cards / Archive rows)
// Read-only, office roles only (the hub decides). The billable numbers are billing's own (app.py _xw_job_delay).
import { useEffect, useReducer } from 'react';
import { supabase } from '../supabaseClient';
import { HUB } from './hubDrafts';
import { hubDelayUrl } from '../utils/delayBreakdown';

const token = async () => {
  const { data } = await supabase.auth.getSession();
  return (data && data.session && data.session.access_token) || '';
};

async function call(path, body) {
  const t = await token();
  if (!t) return { ok: false, status: 401, message: 'Not signed in.' };
  try {
    const opt = { headers: { Authorization: 'Bearer ' + t }, redirect: 'manual' };
    if (body !== undefined) { opt.method = 'POST'; opt.headers['Content-Type'] = 'application/json'; opt.body = JSON.stringify(body); }
    const r = await fetch(HUB + path, opt);
    if (r.type === 'opaqueredirect' || r.status === 0) return { ok: false, status: 401, message: 'The hub did not accept this sign-in.' };
    let j = null;
    try { j = await r.json(); } catch (e) { j = null; }
    if (r.status === 200 && j && j.success) return { ok: true, data: j };
    return { ok: false, status: r.status, message: (j && (j.message || j.error)) || ('The hub answered ' + r.status + '.') };
  } catch (e) {
    return { ok: false, status: 0, message: 'The hub could not be reached (' + ((e && e.message) || 'network') + ').' };
  }
}

export async function fetchDelayBreakdown(loadId, withTrello = true) {
  const r = await call('/api/board/delay-breakdown/' + encodeURIComponent(loadId) + (withTrello ? '' : '?trello=0'));
  return r.ok ? { ok: true, view: r.data.view } : r;
}

// The hub page for an adjustment (Hauling Board tab breakdown) or the delay analyzer, in a new tab, with the session.
export async function openDelayInHub(loadId, what) {
  const win = window.open('about:blank', '_blank');       // opened in the click, so it is not blocked as a pop-up
  const { data } = await supabase.auth.getSession();
  const s = data && data.session;
  const url = hubDelayUrl(HUB, loadId, what, s && s.access_token, s && s.refresh_token);
  if (win) { try { win.opener = null; } catch (e) { /* ignore */ } win.location.href = url; } else window.location.href = url;
}

// ---- badges: one batched request for every card / row on screen, kept 3 minutes
const BADGE_TTL_MS = 3 * 60 * 1000;
const badges = new Map();          // id -> { min, at }
const listeners = new Set();
let pending = new Set();
let timer = null;
let offForThisLogin = false;       // 403: not an office login - stop asking

const notify = () => listeners.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });

async function flush() {
  timer = null;
  const ids = Array.from(pending).slice(0, 300);
  pending = new Set(Array.from(pending).slice(300));
  if (pending.size) timer = setTimeout(flush, 60);
  if (!ids.length || offForThisLogin) return;
  const r = await call('/api/board/delay-badges', { ids });
  const now = Date.now();
  if (r.ok) {
    const got = (r.data && r.data.badges) || {};
    ids.forEach((id) => badges.set(id, { min: Number(got[id]) || 0, at: now }));
    notify();
  } else if (r.status === 403) {
    offForThisLogin = true;
  } else {
    ids.forEach((id) => { if (!badges.has(id)) badges.set(id, { min: 0, at: now - BADGE_TTL_MS + 30000 }); });   // retry in 30 s
  }
}

export function requestDelayBadge(id) {
  if (!id || offForThisLogin) return;
  const hit = badges.get(id);
  if (hit && Date.now() - hit.at < BADGE_TTL_MS) return;
  pending.add(id);
  if (!timer) timer = setTimeout(flush, 60);
}

export const delayBadgeMinutes = (id) => ((badges.get(id) || {}).min || 0);

// Forget one job's badge (after its breakdown was read, the Delay tab's total wins).
export function setDelayBadge(id, min) {
  if (!id) return;
  badges.set(id, { min: Number(min) || 0, at: Date.now() });
  notify();
}

export function useDelayBadge(id) {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    listeners.add(force);
    requestDelayBadge(id);
    return () => { listeners.delete(force); };
  }, [id]);
  return delayBadgeMinutes(id);
}
